import { ICalculation } from "@portal/types/robot/ICalculation";
import { RiskLevel } from "@portal/types/robot/IRobotEnums";
import { ISavingCalculations } from "@portal/types/robot/ISavingCalculations";

export const GetSavingCalculations = (riskLevel: RiskLevel | null, response: ISavingCalculations) => {
  if (!riskLevel) return null;

  const values = Object.values(RiskLevel);

  if (riskLevel === values[0]) return response?.careful;
  if (riskLevel === values[1]) return response?.balanced;
  if (riskLevel === values[2]) return response?.offensive;
  if (riskLevel === values[3]) return response?.extraOffensive;
  return null;
};

export const GetReturnsWithFee = (returnWithoutFee: number | undefined, summedFee: number | undefined) => {
  if (returnWithoutFee === undefined || summedFee === undefined) return 0;
  return returnWithoutFee - summedFee;
};

export const GetManagementFee = (summedFeePercentage: number | undefined, depotFeePercentage: number | undefined) => {
  if (summedFeePercentage === undefined || depotFeePercentage === undefined) return 0;
  return Math.fround(summedFeePercentage - depotFeePercentage);
};

export const GetManagementFeeForDecade = (managmentFeeAmount: number | undefined, refundProvisionAmount: number | undefined) => {
  if (managmentFeeAmount === undefined || refundProvisionAmount === undefined) return 0;
  return Math.fround(managmentFeeAmount - refundProvisionAmount);
};

export const GetCalculationYears = (calculations: Record<string, ICalculation> | undefined) => {
  if (!calculations) return [];

  const entries = Object.entries(calculations);
  const startDate = entries[1][0];
  const startYear = Number(startDate.substring(6, 12));

  const yearOneDate = startDate.substring(0, 6) + (startYear + 1);
  const yearFiveDate = startDate.substring(0, 6) + (startYear + 5);
  const yearTenDate = startDate.substring(0, 6) + (startYear + 10);

  const yearZero = entries.find(ent => ent[0] === startDate)?.[1];
  const yearOne = entries.find(ent => ent[0] === yearOneDate)?.[1];
  const yearFive = entries.find(ent => ent[0] === yearFiveDate)?.[1];
  const yearTen = entries.find(ent => ent[0] === yearTenDate)?.[1];

  return [withdrawYearZero(yearOne, yearZero), withdrawYearZero(yearFive, yearZero), withdrawYearZero(yearTen, yearZero)];
};

const withdrawYearZero = (
  currentYear: ICalculation | undefined,
  yearZero: ICalculation | undefined,
): ICalculation | undefined => {
  if (!currentYear || !yearZero) return undefined;

  return {
    depotFeeAmount: currentYear.depotFeeAmount - yearZero.depotFeeAmount,
    managementFeeAmount: currentYear.managementFeeAmount - yearZero.managementFeeAmount,
    refundProvisionAmount: currentYear.refundProvisionAmount - yearZero.refundProvisionAmount,
    totalAmount: currentYear.totalAmount,
    returnsAmount: currentYear.returnsAmount - yearZero.returnsAmount,
    returnsPercentage: currentYear.returnsPercentage - yearZero.returnsPercentage,
    totalAmountWithFees: currentYear.totalAmountWithFees,
  };
};

export const GetDepotFees = (calculations: Record<string, ICalculation> | undefined) => {
  if (!calculations) return [];

  const years = GetCalculationYears(calculations);

  return [
    years[0] ? Math.floor(years[0].depotFeeAmount) : 0,
    years[1] ? Math.floor(years[1].depotFeeAmount) : 0,
    years[2] ? Math.floor(years[2].depotFeeAmount) : 0,
  ];
};

export const GetManagementFees = (calculations: Record<string, ICalculation> | undefined) => {
  if (!calculations) return [];

  const years = GetCalculationYears(calculations);

  return [
    years[0] ? Math.floor(years[0].managementFeeAmount) : 0,
    years[1] ? Math.floor(years[1].managementFeeAmount) : 0,
    years[2] ? Math.floor(years[2].managementFeeAmount) : 0,
  ];
};

export const GetRefundProvisionAmounts = (calculations: Record<string, ICalculation> | undefined) => {
  if (!calculations) return [];

  const years = GetCalculationYears(calculations);

  return [
    years[0] ? Math.floor(years[0].refundProvisionAmount) : 0,
    years[1] ? Math.floor(years[1].refundProvisionAmount) : 0,
    years[2] ? Math.floor(years[2].refundProvisionAmount) : 0,
  ];
};

export const GetPortfolioValues = (calculations: Record<string, ICalculation> | undefined) => {
  if (!calculations) return [];

  const years = GetCalculationYears(calculations);

  return [
    years[0] ? Math.floor(years[0].totalAmount) : 0,
    years[1] ? Math.floor(years[1].totalAmount) : 0,
    years[2] ? Math.floor(years[2].totalAmount) : 0,
  ];
};

export const GetTotalValuesWithFees = (calculations: Record<string, ICalculation> | undefined) => {
  if (!calculations) return [];

  const years = GetCalculationYears(calculations);
  return [
    years[0] ? Math.floor(years[0].totalAmountWithFees) : 0,
    years[1] ? Math.floor(years[1].totalAmountWithFees) : 0,
    years[2] ? Math.floor(years[2].totalAmountWithFees) : 0,
  ];
};

export const GetDatesForChart = (calculations: Record<string, ICalculation> | undefined) => {
  if (!calculations) return [];

  const entries = Object.entries(calculations);
  const startYear = Number(entries[0][0].substring(12, 6));

  const startDate =
    entries.find(ent => Number(ent[0].substring(12, 6)) === startYear && ent[0].substring(0, 5) === "31.12")?.[0] ?? "";

  const yearFive =
    entries.find(ent => Number(ent[0].substring(12, 6)) === startYear + 5 && ent[0].substring(0, 5) === "31.12")?.[0] ?? "";

  const yearTen =
    entries.find(ent => Number(ent[0].substring(12, 6)) === startYear + 10 && ent[0].substring(0, 5) === "31.12")?.[0] ?? "";

  return [startDate, yearFive, yearTen];
};
